import React, { useEffect, useMemo, useState } from 'react'
import { DatePicker } from '../date-picker'
import { DataType, DayPickerProps } from '../date-picker/types'
import { YearPagination } from '../landing-page/availability-tab/ModalFilterPlanByCalendar'
import { programEndDate } from '../../../app/javascript/utils/plan_duration_calculator'

// プラン詳細（app/views/plans/show.haml）の日付を選ぶカレンダー（#2416）。
// 料金・日付の欄・保存とクリアは date-range-picker controller（app/javascript/controllers/date_range_picker_controller.js）が受け持つ。
// 日付を選んだら plan-calendar:select を送り、controller の clear() から plan-calendar:clear を受け取る。
export const MOUNT_ID = 'plan-calendar'

const DAY_MS = 24 * 60 * 60 * 1000

const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const toDateString = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

// 年度（4 月〜翌 3 月）。LP の空き状況タブと同じ区切り
const fiscalYearOf = (date: Date): number => (date.getMonth() < 3 ? date.getFullYear() - 1 : date.getFullYear())

type StayRule = { roomless: boolean; stayDays: number; programDays: number }

// date-range-picker controller の endDateFor と同じ規則で滞在帯の終了日を求め、帯の日数（終了日 - 開始日 + 1）にする
export const stayDayFor = (start: Date, { roomless, stayDays, programDays }: StayRule): number => {
  const end = roomless
    ? programEndDate(start, programDays)
    : new Date(start.getFullYear(), start.getMonth(), start.getDate() + stayDays)
  return Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1
}

export const PlanCalendar = () => {
  const mount = document.getElementById(MOUNT_ID) as HTMLElement
  // 値は date-range-picker controller の要素から読む。選んだ日（start-date）は controller が書き換えるので、
  // 「戻る」で Turbo が復元したページでも、選んでいた日を出せる
  const picker = (mount.closest('[data-controller~="date-range-picker"]') as HTMLElement).dataset
  const roomless = picker.dateRangePickerRoomlessValue === 'true'
  const stayDays = parseInt(picker.dateRangePickerStayDaysValue ?? '') || 0
  const programDays = parseInt(picker.dateRangePickerProgramDaysValue ?? '') || 0
  const enabledDates = picker.dateRangePickerEnabledDatesValue

  // 選べる日は販売中の日だけ（plans_controller の @array_check_in_dates）。日付ごとの価格は出さない
  const data: DataType[] = useMemo(
    () =>
      (enabledDates ? enabledDates.split(',') : []).map((date) => ({
        date,
        status: 'OK',
        price: '',
        stayDay: stayDayFor(parseLocalDate(date), { roomless, stayDays, programDays }),
      })),
    [enabledDates, roomless, stayDays, programDays],
  )
  const years = useMemo(
    () => [...new Set(data.map((item) => fiscalYearOf(parseLocalDate(item.date))))].sort((a, b) => a - b),
    [data],
  )

  const startDate = picker.dateRangePickerStartDateValue
  const initialDate = startDate && data.some((item) => item.date === startDate) ? parseLocalDate(startDate) : undefined
  // ?month=N（今月から N か月先）を付けて開いたときは、その月の年度から出す
  const monthOffset = parseInt(picker.dateRangePickerMonthValue ?? '')
  const now = new Date()
  const monthTarget = Number.isNaN(monthOffset) ? undefined : new Date(now.getFullYear(), now.getMonth() + monthOffset, 1)
  const initialYear = [monthTarget, initialDate]
    .flatMap((date) => (date ? [fiscalYearOf(date)] : []))
    .find((year) => years.includes(year))

  const [currentYear, setCurrentYear] = useState<number>(initialYear ?? years[0])
  const [initDate, setInitDate] = useState<Date | undefined>(initialDate)
  // クリアしたら作り直して、DatePicker が持っている選択を消す
  const [resetKey, setResetKey] = useState(0)

  useEffect(() => {
    const clear = () => {
      setInitDate(undefined)
      setResetKey((key) => key + 1)
    }
    mount.addEventListener('plan-calendar:clear', clear)
    return () => mount.removeEventListener('plan-calendar:clear', clear)
  }, [])

  if (data.length === 0) return null

  // DatePicker は選んだ日を Date で渡す（型は number | string だが、start_date のときは Date）
  const handleChange: DayPickerProps['onChangeDataSearch'] = (key, value) => {
    const date = value as unknown
    if (key !== 'start_date' || !(date instanceof Date)) return
    mount.dispatchEvent(new CustomEvent('plan-calendar:select', { bubbles: true, detail: { date: toDateString(date) } }))
  }

  return (
    <>
      {years.length > 1 && <YearPagination currentYear={currentYear} availableYears={years} setCurrentYear={setCurrentYear} />}
      {/* @ts-expect-error react-day-picker の型は required のとき selected を求めるが、選んだ日は DatePicker が持つ */}
      <DatePicker
        key={resetKey}
        mode="single"
        required
        full
        showHolidayCaption={false}
        data={data}
        stayDay={Math.max(...data.map((item) => item.stayDay))}
        initDate={initDate}
        startMonth={new Date(currentYear, 3, 1)}
        endMonth={new Date(currentYear + 1, 2, 1)}
        onChangeDataSearch={handleChange}
      />
    </>
  )
}
